import React, { useMemo, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { IConfigurationSummary } from 'pages/Planning/hooks/useConfigurationData'
import './style.css'
import StudyDistributionChart from './StudyDistributionChart'
import { createColorMap } from 'pages/Planning/utils/chartColors'
import { generateSuggestedPlanName } from 'pages/Planning/utils/helpers'
import { RootState } from 'store/modules/rootReducer'

interface IConfigurationSummaryProps {
  summary: IConfigurationSummary
  onEdit: (stepId: string) => void
  onFinish: () => Promise<void>
  isSubmitting: boolean
  isDarkMode?: boolean
  onNameChange: (name: string) => void
  mode?: 'view' | 'create' | 'edit'
}

interface SubjectStats {
  ref: string
  name: string
  color: string
  totalTime: number
}

const ConfigurationSummary: React.FC<IConfigurationSummaryProps> = ({
  summary,
  onEdit,
  onFinish,
  isSubmitting,
  isDarkMode = false,
  onNameChange,
  mode = 'create',
}) => {
  // Get user name from Redux state
  const { profile } = useSelector((state: RootState) => state.student)

  // Track if we've already suggested a name for the current contest
  const suggestedNameRef = useRef<string | null>(null)

  // Generate and set suggested plan name when creating a new plan
  useEffect(() => {
    // Only suggest name when:
    // 1. Mode is 'create'
    // 2. Current name is empty
    // 3. User profile exists
    // 4. Contest is selected
    // 5. We haven't already suggested a name for this contest
    const contestId = summary.contest?._id
    const shouldSuggest =
      mode !== 'edit' &&
      !summary.name.trim() &&
      profile?.name &&
      summary.contest?.publicTender &&
      suggestedNameRef.current !== contestId

    if (shouldSuggest) {
      const suggestedName = generateSuggestedPlanName(profile.name, summary.contest.publicTender)
      if (suggestedName) {
        onNameChange(suggestedName)
        suggestedNameRef.current = contestId || null
      }
    }

    // Reset the ref if contest changes or name is manually cleared
    if (contestId && suggestedNameRef.current !== contestId) {
      suggestedNameRef.current = null
    }
  }, [
    mode,
    summary.name,
    summary.contest?._id,
    summary.contest?.publicTender,
    profile?.name,
    onNameChange,
  ])

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) {
      return `${minutes}min`
    } else if (mins === 0) {
      return `${hours}h`
    } else {
      return `${hours}h ${mins}min`
    }
  }

  // Process study distribution data
  const studyDistribution = useMemo(() => {
    // Check if we have all required data
    if (
      !summary.selectedDisciplines.length ||
      !summary.weeklyHours ||
      !summary.sessionDurationRange
    ) {
      return null
    }

    try {
      // Map disciplines to subjects format expected by fragmentation
      const subjects = summary.selectedDisciplines.map((discipline) => ({
        ref: discipline.ref,
        name: discipline.subject,
        weight: discipline.weight, // Weight is already calculated (0-100 scale)
      }))

      // Get unique subjects to assign consistent colors
      const uniqueSubjects: string[] = Array.from(new Set(subjects.map((s) => String(s.ref))))

      // Create color map using shared chart colors utility
      const subjectColorMap = createColorMap(uniqueSubjects)

      // Map each session with its number and consistent color
      const chartSessions = subjects.map((subject, idx) => ({
        _id: subject.ref,
        module: {
          _id: subject.ref,
          subject: subject.name,
        },
        name: subject.name,
        weight: subject.weight,
        order: idx + 1,
        hours: (subject.weight / subjects.reduce((a, b) => a + b.weight, 0)) * summary.weeklyHours,
        status: 'pending',
        color: subjectColorMap.get(subject.ref) || '#3366CC',
      }))

      // Create subject stats map for easy access during iteration
      // Maps subject name to its color, total time, and session count
      const subjectStatsMap = new Map<string, SubjectStats>(
        subjects.map((subject) => [
          subject.ref,
          {
            ref: subject.ref,
            name: subject.name,
            color: subjectColorMap.get(subject.ref) || '#3366CC',
            totalTime:
              (subject.weight / subjects.reduce((a, b) => a + b.weight, 0)) * summary.weeklyHours, // Total hours allocated
          },
        ])
      )

      return {
        sessions: chartSessions,
        totalHours: summary.weeklyHours,
        subjectColorMap, // Mapa de cores para usar na lista de disciplinas
        subjectStatsMap, // Mapa com todos os dados organizados por disciplina
      }
    } catch (error) {
      console.error('Error processing study distribution:', error)
      return null
    }
  }, [summary.selectedDisciplines, summary.weeklyHours, summary.sessionDurationRange])

  return (
    <div className={`configuration-summary ${isDarkMode ? 'dark' : 'light'}`} data-tour="summary">
      <div className="summary-header">
        <h3 className="summary-title">Resumo da sua configuração</h3>
        <p className="summary-subtitle">Revise suas escolhas antes de finalizar o planejamento</p>
      </div>

      {/* Planning Name Input */}
      <div className="summary-section planning-name-section">
        <div className="summary-section-header">
          <h4 className="summary-section-title">Nome do planejamento *</h4>
        </div>
        <div className="summary-section-content">
          <input
            type="text"
            className="planning-name-input"
            data-tour="plan-name"
            placeholder="Ex: Preparação TRF - 2025"
            value={summary.name}
            onChange={(e) => onNameChange(e.target.value)}
            maxLength={100}
          />
          {summary.name.trim() === '' && (
            <p className="input-hint error">O nome do planejamento é obrigatório</p>
          )}
          {summary.name.trim() !== '' && (
            <p className="input-hint">
              Escolha um nome que facilite a identificação do seu plano de estudos
            </p>
          )}
        </div>
      </div>

      <div className="summary-content">
        <div className="summary-grid">
          {/* Contest Summary */}
          <div className="summary-section" data-tour="summary-contest">
            <div className="summary-section-header">
              <h4 className="summary-section-title">Concurso de interesse</h4>
              <button className="edit-button" onClick={() => onEdit('step-1')} type="button">
                Editar
              </button>
            </div>
            <div className="summary-section-content">
              {summary.contest ? (
                <div className="contest-summary">
                  <div className="contest-icon">
                    {summary.contest.icon ? (
                      <img src={summary.contest.icon} alt={summary.contest.publicTender} />
                    ) : (
                      <div className="contest-option-fallback-icon">
                        {summary.contest.publicTender
                          .trim()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/-/g, '')
                          .split(' ')
                          .filter((p) => p.length > 2)
                          .map((p) => p[0].toUpperCase())
                          .join('')}
                      </div>
                    )}
                  </div>
                  <div className="contest-details">
                    <h5 className="contest-name">{summary.contest.publicTender}</h5>
                    <p className="contest-role">{summary.contest.role}</p>
                    <p className="contest-career">{summary.contest.career}</p>
                  </div>
                </div>
              ) : (
                <p className="no-selection">Nenhum concurso selecionado</p>
              )}
            </div>
          </div>

          {/* Weekly Schedule Summary */}
          <div className="summary-section span-2" data-tour="summary-organization">
            <div className="summary-section-header">
              <h4 className="summary-section-title">Organização de estudos</h4>
              <button className="edit-button" onClick={() => onEdit('step-3')} type="button">
                Editar
              </button>
            </div>
            <div className="summary-section-content">
              <div className="weekly-schedule-summary">
                <div className="schedule-stats">
                  {/* <div className="stat-item">
                    <span className="stat-value">
                      {summary.organizationType === 'cycle'
                        ? 'Ciclos de estudos'
                        : 'Cronograma semanal'}
                    </span>
                    <span className="stat-label">Tipo de planejamento</span>
                  </div> */}
                  <div className="stat-item">
                    <span className="stat-value">{summary.weeklyHours}h</span>
                    <span className="stat-label">Carga horária semanal</span>
                  </div>
                  {summary.organizationType === 'schedule' && (
                    <div className="stat-item">
                      <span className="stat-value">{summary.selectedDays.length}</span>
                      <span className="stat-label">Dias selecionados</span>
                    </div>
                  )}
                  <div className="stat-item">
                    <span className="stat-value">
                      {formatDuration(summary.sessionDurationRange.minDuration)} -{' '}
                      {formatDuration(summary.sessionDurationRange.maxDuration)}
                    </span>
                    <span className="stat-label">Duração das sessões</span>
                  </div>
                </div>
                {/* <div className="schedule-days">
                <WeekCalendar
                  weeklySchedule={summary.weeklySchedule}
                  weeklyHours={summary.weeklyHours}
                  isDarkMode={isDarkMode}
                />
              </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Disciplines Summary */}
        <div className="summary-grid">
          <div className="summary-section span-2" data-tour="summary-disciplines">
            <div className="summary-section-header">
              <h4 className="summary-section-title">
                Disciplinas selecionadas ({summary.selectedDisciplines.length}/{summary.disciplines}
                )
              </h4>
              <button className="edit-button" onClick={() => onEdit('step-2')} type="button">
                Editar
              </button>
            </div>
            <div className="summary-section-content">
              <div className="disciplines-summary-container">
                {studyDistribution?.sessions.length > 0 ? (
                  <div className="disciplines-summary">
                    {studyDistribution?.sessions.map((session, index) => {
                      return (
                        <div key={session._id} className="discipline-item">
                          <div className="discipline-info">
                            <span
                              className="discipline-bullet"
                              style={{ backgroundColor: session.color || '#3366CC' }}
                            />
                            <span className="discipline-index">{index + 1}º</span>
                            <span className="discipline-subject">{session.module.subject}</span>
                          </div>
                          <div className="discipline-stats">
                            <div className="discipline-knowledge">
                              <span className="discipline-index">{session.weight}%</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="no-selection">Nenhuma disciplina selecionada</p>
                )}
              </div>
            </div>
          </div>
          {/* Study Distribution Chart */}
          {studyDistribution && (
            <div className="summary-section height-fit-content" data-tour="summary-distribution">
              <div className="summary-section-header">
                <h4 className="summary-section-title">Distribuição por disciplina</h4>
              </div>
              <div className="summary-section-content">
                <StudyDistributionChart
                  sessions={studyDistribution.sessions}
                  totalHours={studyDistribution.totalHours}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="summary-actions">
        <button
          className="create-planning-button primary"
          data-tour="finish-button"
          onClick={onFinish}
          disabled={isSubmitting || !summary.name.trim()}
          type="button"
        >
          {isSubmitting ? (
            <>
              <span className="loading-spinner"></span>
              {mode === 'edit' ? 'Salvando planejamento...' : 'Criando planejamento...'}
            </>
          ) : mode === 'edit' ? (
            'Salvar planejamento'
          ) : (
            'Criar planejamento'
          )}
        </button>
      </div>
    </div>
  )
}

export default ConfigurationSummary
